import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useRecoilState, useRecoilValue } from "recoil";
import useIsIOS from "../hooks/useIsIOS";
import useKeysPressed from "../hooks/useKeysPressed";
import {
  currentStationIdState,
  currentStationIndexState,
  currentStationState,
  disableShortcutState,
  embedShownState,
  isBufferingState,
  playerShownState,
  playerVolumeState,
  stationsSelectorOpenState,
} from "../recoilState";
import stations from "../stations";
import { trackEvent } from "../utils/analytics";
import BlinkingDots from "./BlinkingDots";
import Button from "./Button";
import Selector from "./Selector";
import Spacer from "./Spacer";
import VolumeSlider from "./VolumeSlider";

const UNPLAYABLE_ERRORS = [100, 101, 150];

function Player({ onStationChanged, isPlaying, setIsPlaying, onListeningChange }) {
  const show = useRecoilValue(playerShownState);
  const shortcutsDisabled = useRecoilValue(disableShortcutState);
  const activeKeyPresses = !shortcutsDisabled;

  const [stationsSelectorOpen, setStationsSelectorOpen] = useRecoilState(
    stationsSelectorOpenState
  );

  const [currentStationId, setCurrentStationId] = useRecoilState(
    currentStationIdState
  );
  const currentStation = useRecoilValue(currentStationState);
  const currentStationIndex = useRecoilValue(currentStationIndexState);

  const [reactPlayerLoading, setReactPlayerLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useRecoilState(isBufferingState);
  const [longBuffering, setLongBuffering] = useState(null);
  const [playerVolume, setPlayerVolume] = useRecoilState(playerVolumeState);
  const [error, setError] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [playingSession, setPlayingSession] = useState(null);
  const hasTrackedListening = useRef(false);
  const activeSession = useRef(null);
  if (activeSession.current?.stationId !== currentStation.id) {
    activeSession.current = { stationId: currentStation.id };
  }
  const session = activeSession.current;
  const playerShown = useRecoilValue(playerShownState);

  const [_embedShown, setEmbedShown] = useRecoilState(embedShownState);
  let embedShown = !error && playerShown && _embedShown;

  const isIOS = useIsIOS();

  // Play intent can precede autoplay failure or a station that never starts.
  const isListening = Boolean(
    isPlaying && playingSession === session && !isBuffering
  );
  useEffect(() => {
    onListeningChange(isListening);
    return () => onListeningChange(false);
  }, [isListening, onListeningChange]);

  useEffect(() => {
    if (!isPlaying) setPlayingSession(null);
  }, [isPlaying]);

  useEffect(() => {
    activeSession.current = session;
    return () => {
      if (activeSession.current === session) activeSession.current = null;
    };
  }, [session]);

  // Events for the previous station can still arrive while the player switches videos.
  function forCurrentStation(callback) {
    return (...args) => {
      if (activeSession.current === session) callback(...args);
    };
  }

  function resetError() {
    setError(null);
    setErrorMsg("");
  }

  const handleShufflePlay = useCallback(
    (userInitiated = false) => {
      resetError();
      setStationsSelectorOpen(false);
      const randomStationIndex = getRandomIndex(stations, currentStationIndex);
      const stationId = stations[randomStationIndex].id;
      setCurrentStationId(stationId);
      onStationChanged();
      if (userInitiated) {
        trackEvent("Station Selected", { stationId, action: "shuffle" });
      }
    },
    [
      currentStationIndex,
      onStationChanged,
      setCurrentStationId,
      setStationsSelectorOpen,
    ]
  );

  function handleGoBack() {
    resetError();
    setStationsSelectorOpen(false);
    const prevStationIndex = getPrevIndex(stations, currentStationIndex);
    const stationId = stations[prevStationIndex].id;
    setCurrentStationId(stationId);
    onStationChanged();
    trackEvent("Station Selected", { stationId, action: "previous" });
  }

  function handleGoForward() {
    resetError();
    setStationsSelectorOpen(false);
    const nextStationIndex = getNextIndex(stations, currentStationIndex);
    const stationId = stations[nextStationIndex].id;
    setCurrentStationId(stationId);
    onStationChanged();
    trackEvent("Station Selected", { stationId, action: "next" });
  }

  function volumeUp() {
    setPlayerVolume(Math.round(Math.min(playerVolume + 0.1, 1) * 10) / 10);
  }

  function volumeDown() {
    setPlayerVolume(Math.round(Math.max(playerVolume - 0.1, 0) * 10) / 10);
  }

  function handleShowOriginalVideo() {
    setEmbedShown(!embedShown);
  }

  useKeysPressed(
    [
      ["ArrowRight", handleGoForward],
      ["ArrowLeft", handleGoBack],
      ["ArrowUp", volumeUp],
      ["ArrowDown", volumeDown],
      ["v", handleShowOriginalVideo],
      ["Escape", () => setStationsSelectorOpen(false)],
      [" ", () => setIsPlaying(!isPlaying)],
    ],
    show && activeKeyPresses
  );

  /* ----------------------------- Handle Loading ----------------------------- */

  useEffect(() => {
    let timeout;
    if (isBuffering) {
      timeout = setTimeout(() => {
        setLongBuffering(true);
      }, 3000);
      return () => clearTimeout(timeout);
    } else {
      clearTimeout(timeout);
      setLongBuffering(false);
    }
  }, [isBuffering, currentStation]);

  /* ------------------------------ Handle Error ------------------------------ */

  useEffect(() => {
    // YouTube's "can't play here" codes: 100 removed or private (ended 24/7 streams), 101/150 embedding blocked.
    if (UNPLAYABLE_ERRORS.includes(error)) {
      setErrorMsg("Ops, this station is not working. Skipping it");
      const timeout = setTimeout(() => {
        resetError();
        handleShufflePlay();
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [error, handleShufflePlay]);

  return (
    <>
      <div style={{ ...containerInnerStyle, display: show ? "block" : "none" }}>
        {!stationsSelectorOpen && errorMsg && (
          <span className="red">
            {errorMsg}
            <BlinkingDots />
          </span>
        )}
        {!stationsSelectorOpen && !errorMsg && isBuffering && longBuffering && (
          <span className="red">
            Buffering
            <BlinkingDots />
          </span>
        )}
        <Spacer size={14} />
        {!reactPlayerLoading && (
          <div id="buttons-ui">
            <div style={buttonsStyle}>
              {!isPlaying && (
                <Button icon="play" onClick={() => setIsPlaying(true)} />
              )}
              {isPlaying && (
                <Button icon="pause" onClick={() => setIsPlaying(false)} />
              )}
              <Button icon="shuffle" onClick={() => handleShufflePlay(true)} />
              <Button icon="previous" onClick={handleGoBack} />
              <Button icon="forward" onClick={handleGoForward} />
              {!isIOS && (
                <VolumeSlider
                  volume={playerVolume}
                  setVolume={setPlayerVolume}
                  style={{ marginLeft: "4px" }}
                />
              )}
            </div>
          </div>
        )}
        <Spacer size={12} />
        {!reactPlayerLoading && (
          <Selector
            isPlaying={isPlaying}
            currentStationId={currentStationId}
            setCurrentStationId={(station) => {
              setCurrentStationId(station);
              onStationChanged();
              setIsPlaying(true);
              if (station !== currentStation.id) {
                trackEvent("Station Selected", {
                  stationId: station,
                  action: "selector",
                });
              }
            }}
          />
        )}
        {reactPlayerLoading && (
          <div id="buttons-ui">
            <LoadingText />
          </div>
        )}
      </div>
      <div
        style={embedShown ? wrapperStyle : hiddenStyle}
        className="yt-wrapper"
      >
        <div style={innerWrapperStyle}>
          {/* No key: one persistent iframe lets YouTube switch stations inside the frame the user's tap
              already unlocked. A fresh iframe per station starts silent on iOS until the user taps play. */}
          <ReactPlayer
            controls={false}
            playing={isPlaying}
            url={"https://www.youtube.com/watch?v=" + currentStation.id}
            style={reactPlayerStyle}
            width="100vw"
            height="200vw"
            volume={playerVolume}
            config={{
              youtube: {
                playerVars: {
                  modestbranding: true,
                  color: "black",
                },
              },
            }}
            playsinline={true}
            onReady={forCurrentStation(() => setReactPlayerLoading(false))}
            onError={forCurrentStation((err) => {
              setPlayingSession(null);
              setError(err);
            })}
            onPlay={forCurrentStation(() => {
              setPlayingSession(session);
              setIsBuffering(false);
              setIsPlaying(true);
              if (!hasTrackedListening.current) {
                hasTrackedListening.current = true;
                trackEvent("Listening Started", { stationId: currentStation.id });
              }
            })}
            onPause={forCurrentStation(() => {
              setPlayingSession(null);
              setIsPlaying(false);
            })}
            onBuffer={forCurrentStation(() => setIsBuffering(true))}
            onBufferEnd={forCurrentStation(() => setIsBuffering(false))}
            onStart={forCurrentStation(() => setIsBuffering(false))}
            onEnded={forCurrentStation(() => {
              setPlayingSession(null);
              handleShufflePlay();
            })}
          />
        </div>
      </div>
    </>
  );
}

export function getRandomIndex(arr, currentIndex) {
  const randomIndex = Math.floor(Math.random() * arr.length);

  return randomIndex === currentIndex
    ? getRandomIndex(arr, currentIndex)
    : randomIndex;
}
function getNextIndex(arr, currentIndex) {
  const nextIndex = currentIndex + 1;
  return nextIndex > arr.length - 1 ? 0 : nextIndex;
}
function getPrevIndex(arr, currentIndex) {
  const prevIndex = currentIndex - 1;
  return prevIndex < 0 ? arr.length - 1 : prevIndex;
}

export const containerInnerStyle = {
  width: "100%",
  zIndex: 6,
};

const buttonsStyle = {
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
};

const wrapperStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 0,
  background: "black",
};

const hiddenStyle = {
  pointerEvents: "none",
  userSelect: "none",
  position: "fixed",
  top: "100%",
  left: "100%",
};

const innerWrapperStyle = {
  width: "100%",
  height: "100%",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
};

const reactPlayerStyle = {
  pointerEvents: "none",
  userSelect: "none",
  zIndex: -1,
  borderRadius: "8px",
};

const LoadingText = () => {
  const [dots, setDots] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (dots === "") setDots(".");
      if (dots === ".") setDots("..");
      if (dots === "..") setDots("...");
      if (dots === "...") setDots("");
    }, 300);
    return () => clearTimeout(timeout);
  }, [dots]);

  return <span>Loading{dots}</span>;
};
export default Player;
